// Pure helpers for the Difference Checker (SbDiffCheckerV3), kept out of the component so they can be
// unit tested without rendering it.

// Nothing differs between the two versions being compared: the right pane is empty, or (in Approval
// Review) every block on both sides is unchanged.
export const isNoDiff = (diffData, isApproverView = false) => {
  const diff = diffData?.diff;
  if (!diff) return false;
  const right = diff.right_pane || [];
  const left = diff.left_pane || [];
  return right.length === 0 || (
    isApproverView &&
    left.every((item) => item.status === 'unchanged') &&
    right.every((item) => item.status === 'unchanged')
  );
};

// What the checker shows when nothing differs: the version already on the page (the one the editor
// holds), not a second copy fetched from the server (the old "original JD" lookup is gone).
export const getNoDiffMarkdown = (editableMarkdown) => (typeof editableMarkdown === 'string' ? editableMarkdown : '');

// The version list comes back from the backend newest first (/approval/history_list), so the default
// comparison is the two newest versions: the previous one on the left, the newest on the right. With a
// single version both sides are that version. The UI never sorts or compares version numbers.
export const getDefaultPair = (options) => {
  if (!Array.isArray(options) || options.length === 0) return { left: null, right: null };
  if (options.length === 1) return { left: options[0].value, right: options[0].value };
  return { left: options[1].value, right: options[0].value };
};

// Position in the backend's newest-first list: a smaller index is a newer version.
export const newerIndexFirst = (options, value) => options.findIndex((o) => o.value === value);

// Left must always be older than right. Both lists are in the backend's order, so "older" means a
// larger index. The left choices are the versions older than `right`, the right choices those newer
// than `left`; before the user has picked anything the newest is held back from the left list and the
// oldest from the right one.
export const getLeftOptions = (options, right, hasInteracted) => {
  if (options.length <= 1) return options;
  if (!hasInteracted) return options.filter((_, i) => i !== 0);
  const rightIndex = newerIndexFirst(options, right);
  if (right == null || rightIndex < 0) return options;
  return options.filter((_, i) => i > rightIndex);
};

export const getRightOptions = (options, left, hasInteracted) => {
  if (options.length <= 1) return options;
  if (!hasInteracted) return options.filter((_, i) => i !== options.length - 1);
  const leftIndex = newerIndexFirst(options, left);
  if (left == null || leftIndex < 0) return options;
  return options.filter((_, i) => i < leftIndex);
};

// The nearest version older than `value` (next in the backend's order), or null.
export const findNearestOlder = (options, value) => {
  const index = newerIndexFirst(options, value);
  return index >= 0 && index + 1 < options.length ? options[index + 1].value : null;
};

// The nearest version newer than `value` (previous in the backend's order), or null.
export const findNearestNewer = (options, value) => {
  const index = newerIndexFirst(options, value);
  return index > 0 ? options[index - 1].value : null;
};

// True when `a` is not older than `b` in the backend's order (a is the same version or newer).
export const isSameOrNewer = (options, a, b) => newerIndexFirst(options, a) <= newerIndexFirst(options, b);
// True when `a` is not newer than `b` (a is the same version or older).
export const isSameOrOlder = (options, a, b) => newerIndexFirst(options, a) >= newerIndexFirst(options, b);
